import { morphism } from 'morphism';
import { createStrictSchema, createUnknownSchema } from '../../utils/morph.js';
import { geoEntryReverseSchema, geoEntrySchema } from '../geo/morph.js';

const userSchema = createStrictSchema({
    userID: 'user_id',
    email: 'email',
    status: 'status',
    creationDate: 'creation_date',
});
const usersSchema = (body) => Array.isArray(body) ? body.map(morphism(userSchema)) : [];
const subscriptionProductSchema = createStrictSchema({
    categories: 'product.categories',
});
const userSubscriptionSchema = createStrictSchema({
    subscriptionId: 'subscription_id',
    statusSubscriptions: 'status_subscriptions',
    startDate: 'start_date',
    endDate: 'end_date',
    product: morphism(subscriptionProductSchema),
});
const userSubscriptionsSchema = createStrictSchema({
    subscriptions: {
        path: 'subscriptions',
        fn: morphism(userSubscriptionSchema),
    },
});
const loggedUserSchema = createStrictSchema({
    userID: 'user_id',
    email: 'email',
    username: 'username',
});
const userProfileInfoSchema = createStrictSchema({
    username: 'name',
    showPhone: 'show_phone',
    isDefaultName: 'is_default_name',
    picture: 'picture',
});
const userProfilePhoneSchema = createStrictSchema({
    show: 'show',
    number: 'number',
    prefix: 'idd_prefix',
});
const userProfilePhoneReverseSchema = createUnknownSchema({
    show: 'show',
    number: 'number',
    idd_prefix: 'prefix',
});
const userProfileReverseSchema = createUnknownSchema({
    borndate: 'birthdate',
    name: 'username',
    gender: 'sex',
    geo: {
        path: 'geo',
        fn: morphism(geoEntryReverseSchema),
    },
    phone: {
        path: 'phone',
        fn: morphism(userProfilePhoneReverseSchema),
    },
});
const userProfileSchema = createStrictSchema({
    birthdate: 'borndate',
    username: 'name',
    sex: 'gender',
    geo: {
        path: 'geo',
        fn: morphism(geoEntrySchema),
    },
    phone: {
        path: 'phone',
        fn: morphism(userProfilePhoneSchema),
    },
});
const userContactInfoSchema = createStrictSchema({
    adURN: 'ad_urn',
    phoneNumber: 'phone_number',
    force: 'force',
});
const badgeStatusSchema = createStrictSchema({
    assignedAt: 'assigned_at',
    label: 'label',
    earned: 'earned',
    currentValue: 'current_value',
    targetValue: 'target_value',
});
const badgeSchema = createStrictSchema({
    id: 'id',
    icon: 'icon',
    name: 'name',
    description: 'description',
    instructions: 'instructions',
    info: 'info',
    infoIcon: 'info_icon',
    isActive: 'is_active',
    categories: {
        path: 'categories',
        fn: (categories) => {
            if (!Array.isArray(categories)) {
                return categories;
            }
            return categories.map((c) => c.toString());
        },
    },
    status: {
        path: 'status',
        fn: morphism(badgeStatusSchema),
    },
});
const userAdInfoSchema = createStrictSchema({
    firstAdInsertionDate: 'first_ad_insertion_date',
    totalApprovedAds: 'total_num_approved_ads',
    userCategoriesInfo: {
        path: 'categories_num_approved_ads',
        fn: (ads) => {
            return ads.map((ad) => ({
                category: ad.category_id.toString(),
                approvedAds: ad.num_approved_ads,
            }));
        },
    },
    badges: {
        path: 'badges',
        fn: (items) => {
            if (!items?.length)
                return;
            return items.map(morphism(badgeSchema));
        },
    },
});
const userEmailHistorySchema = createStrictSchema({
    email: 'email',
    date: 'date',
    channel: 'channel',
    adminId: 'admin_id',
});
const userEmailsHistorySchema = createStrictSchema({
    history: {
        path: 'history',
        fn: morphism(userEmailHistorySchema),
    },
});
const changeEmailSessionSchema = createStrictSchema({
    newEmail: 'new_email',
    id: 'id',
    challenges: {
        path: 'challenges',
        fn: (challenges) => {
            return challenges.map((challenge) => ({
                type: challenge.type,
                methods: challenge.methods,
            }));
        },
    },
});
const changeEmailProcessSchema = createStrictSchema({
    account: {
        email: 'account.email',
        phone: 'account.phone',
        hasSafeSession: 'account.is_safe',
    },
    session: {
        path: 'session',
        fn: (session) => {
            return session ? morphism(changeEmailSessionSchema, session) : undefined;
        },
    },
});
const changeEmail2FAChallengeSchema = createStrictSchema({
    token: 'token',
});
const changeEmailIsEligibleSchema = createStrictSchema({
    isEligible: 'is_eligible',
});
const userDac7InfoSchema = createStrictSchema({
    firstName: 'name',
    lastName: 'surname',
    dateOfBirth: 'date_of_birth',
    countryOfBirth: 'place_of_birth',
    cityOfBirth: 'town_of_birth',
    country: 'country',
    town: 'town',
    city: 'city',
    address: 'address',
    postalCode: 'postal_code',
    taxCode: 'fiscal_code',
    iban: {
        path: 'receiving_payment_methods.iban',
        fn: (value) => value ?? [''],
    },
    paypal: {
        path: 'receiving_payment_methods.paypal',
        fn: (value) => value ?? [''],
    },
    disclaimer: 'user_certified',
});
const isLikeEmptyArray = (array) => array?.every((v) => !v);
const userDac7InfoSaveSchema = createUnknownSchema({
    name: 'firstName',
    surname: 'lastName',
    date_of_birth: 'dateOfBirth',
    place_of_birth: 'countryOfBirth',
    town_of_birth: 'cityOfBirth',
    country: 'country',
    town: 'town',
    city: 'city',
    address: 'address',
    postal_code: 'postalCode',
    fiscal_code: 'taxCode',
    receiving_payment_methods: (values) => ({
        iban: isLikeEmptyArray(values.iban) ? null : values.iban,
        paypal: isLikeEmptyArray(values.paypal) ? null : values.paypal,
    }),
    user_certified: 'disclaimer',
});
const user2FAChallengesSchema = createStrictSchema({
    availableFactors: 'available_factors',
    defaultFactor: 'default',
});
const user2FAChallengeSchema = createStrictSchema({
    remainingAttempts: 'remaining_attempts',
    recipient: 'recipient',
});

export { badgeSchema, badgeStatusSchema, changeEmail2FAChallengeSchema, changeEmailIsEligibleSchema, changeEmailProcessSchema, loggedUserSchema, user2FAChallengeSchema, user2FAChallengesSchema, userAdInfoSchema, userContactInfoSchema, userDac7InfoSaveSchema, userDac7InfoSchema, userEmailsHistorySchema, userProfileInfoSchema, userProfilePhoneReverseSchema, userProfilePhoneSchema, userProfileReverseSchema, userProfileSchema, userSubscriptionsSchema, usersSchema };
//# sourceMappingURL=morph.js.map
